import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';
import { FeeInfo } from '@/components/LargeOrderFee/LargeOrderFeeInfo';
import type { LargeOrderFeeReason, ServiceSwitch } from '@/types';
import { SectionHeader } from './SectionHeader';
import { SummaryRow } from './SummaryRow';

interface PaymentSummarySectionProps {
  paymentMethod: string;
  orderFee: string;
  shippingFee: string;
  customerPays: number;
  currency: string;
  /** BOXY-311 §9.4 — the fee line: the estimate before settlement, the settled figure after. */
  largeOrderFee?: number;
  largeOrderFeeReason?: LargeOrderFeeReason | null;
  /** BOXY-113 §9.3 — the ⓘ beside the shipping fee, only on orders the rule switched. */
  serviceSwitch?: ServiceSwitch | null;
}

export function PaymentSummarySection({
  paymentMethod,
  orderFee,
  shippingFee,
  customerPays,
  currency,
  largeOrderFee,
  largeOrderFeeReason,
  serviceSwitch,
}: PaymentSummarySectionProps) {
  const { t } = useTranslation();

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={CreditCard} title={String(t('payment_summary'))} />

      <div className='divide-y divide-sd-border'>
        <SummaryRow label={String(t('method'))} value={paymentMethod} />
        <SummaryRow label={String(t('order_fee'))} value={orderFee} />
        <SummaryRow label={String(t('shipping_fee_label'))}>
          <span className='inline-flex items-center gap-1.5 text-sm font-medium text-end'>
            {shippingFee || '—'}
            {serviceSwitch ? <FeeInfo title='' message={serviceSwitch.message} /> : null}
          </span>
        </SummaryRow>
        {largeOrderFee && largeOrderFeeReason ? (
          <SummaryRow label={String(t('large_order_fee', 'Large order fee'))}>
            <span
              className='inline-flex items-center gap-1.5 text-sm font-medium text-end'
              data-testid='order-large-order-fee'
            >
              {formatWithThousandSeparator(largeOrderFee)} {currency}
              <FeeInfo title={largeOrderFeeReason.title} message={largeOrderFeeReason.message} />
            </span>
          </SummaryRow>
        ) : null}

        <div className='flex items-center justify-between py-2.5'>
          <span className='text-sm font-semibold'>{String(t('customer_pays'))}</span>
          <span className='text-sm font-bold shrink-0'>
            {formatWithThousandSeparator(customerPays)} {currency}
          </span>
        </div>
      </div>
    </div>
  );
}
